import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axios';

/**
 * Helper function để đảm bảo token được lưu vào localStorage trước khi tiếp tục
 * Đặc biệt quan trọng với Firefox có thể có delay khi ghi localStorage
 */
const ensureTokenSaved = async (token: string, maxRetries: number = 5): Promise<boolean> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Lưu token
      localStorage.setItem('access_token', token);
      
      // Đợi một chút để localStorage được ghi (đặc biệt quan trọng với Firefox)
      await new Promise(resolve => setTimeout(resolve, 100 * attempt)); // Tăng delay mỗi lần retry
      
      // Verify token đã được lưu
      const savedToken = localStorage.getItem('access_token');
      
      if (savedToken === token) {
        return true;
      }
    } catch (error) {
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 100 * attempt));
      }
    }
  }

  return false;
};

const SSOCallback = () => {
  const navigate = useNavigate();
  const { setToken } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      // Lấy token từ URL params
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('access_token');

      if (token) {
        try {
          // QUAN TRỌNG: Đảm bảo token được lưu vào localStorage trước khi tiếp tục
          const tokenSaved = await ensureTokenSaved(token);
          
          if (!tokenSaved) {

            navigate('/login');
            return;
          }
          
          // Gọi setToken để cập nhật state trong AuthContext
          setToken(token);
          
          // Verify lại token sau khi setToken
          const verifiedToken = localStorage.getItem('access_token');
          if (!verifiedToken || verifiedToken !== token) {

            navigate('/login');
            return;
          }
          
          // QUAN TRỌNG: Đợi API response để verify token và lấy user info trước khi navigate
          try {
            // Đảm bảo token vẫn còn trong localStorage trước khi gọi API
            const tokenBeforeApi = localStorage.getItem('access_token');
            if (!tokenBeforeApi || tokenBeforeApi !== token) {

              navigate('/login');
              return;
            }
            
            const response = await axiosInstance.get('/auth/user', {
              headers: {
                'Cache-Control': 'no-cache',
                'Pragma': 'no-cache',
                'Authorization': `Bearer ${tokenBeforeApi}`
              }
            });
            
            if (response?.data) {
              const userData = response.data.data?.user || response.data;
              
              if (userData) {
                // Đợi thêm một chút để đảm bảo state đã được cập nhật
                await new Promise(resolve => setTimeout(resolve, 200));
                
                // Chuyển hướng về trang đã lưu hoặc trang chủ
                let redirectUrl = sessionStorage.getItem('redirectUrl') || '/';
                sessionStorage.removeItem('redirectUrl');
                // Đơn vị tư vấn - nhà thầu (donvi_type === 2) và user có tuvan_nhathau === 1 mới mặc định vào /tuvan_nhathau
                if ((redirectUrl === '/' || !redirectUrl) && userData.donvi_type === 2 && userData.tuvan_nhathau === 1) {
                  redirectUrl = '/tuvan_nhathau';
                }
                
                // Kiểm tra lại token trước khi navigate
                const tokenBeforeNavigate = localStorage.getItem('access_token');
                
                if (tokenBeforeNavigate) {
                  navigate(redirectUrl);
                } else {

                  navigate('/login');
                }
              } else {

                navigate('/login');
              }
            } else {

              navigate('/login');
            }
          } catch (apiError: any) {

            // Nếu lỗi 401, token không hợp lệ
            if (apiError?.response?.status === 401) {
              localStorage.removeItem('access_token');
            }
            
            navigate('/login');
          }
        } catch (error) {

          navigate('/login');
        }
      } else {

        navigate('/login');
      }
    };

    handleCallback();
  }, [navigate, setToken]);

  return null; // Component này không render gì cả
};

export default SSOCallback; 